import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import { queriesPayerRows } from "@query";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";

type Value = string | null;

/**
 * The server maximum per page (`/v3/search/payer-rows` clamps `page[size]` to
 * 100), asked for explicitly rather than taking the default of 25. A picker is
 * scanned, not paged through, so an account with a few hundred clients should
 * reach the end of its list in a couple of scrolls — `onReachMenuEnd` still
 * walks the cursor for the accounts with more than one page of them.
 */
const PAGE_SIZE = 100;

/**
 * `/v3/search/payer-rows` rejects a `filter[searchString][eq]` shorter than two
 * characters with a `ValidationError`, so a single character is held back and
 * the unfiltered list stays on screen until the text is long enough to send.
 */
const MIN_SEARCH_TEXT_LENGTH = 2;

type Props = {
  value: Value;
  onChange: (value: Value) => void;
  defaultSearchText?: string;
} & Omit<WSSelectSearchProps, "options" | "value" | "onChange">;

export const SelectPayer: React.FC<Props> = ({
  value,
  onChange,
  defaultSearchText,
  ...otherProps
}) => {
  // `WSSelectSearch` already debounces its search box before calling
  // `onExternalSearchChange`, so this is the settled text, not a keystroke.
  const [searchText, setSearchText] = useState(defaultSearchText ?? "");
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const trimmedSearchText = searchText.trim();
  const searchString =
    trimmedSearchText.length >= MIN_SEARCH_TEXT_LENGTH
      ? trimmedSearchText
      : undefined;

  const queryPayerRows = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    filter: {
      searchString: searchString ? { eq: searchString } : undefined
    },
    page: { size: PAGE_SIZE }
  });

  // The selected client is read from the same search rows the options come from,
  // so the label of a picked client matches the label it had in the list. A
  // `Payer` read would name it off the raw `profile.displayName` instead of the
  // server-resolved `profile.resolvedName` the rows carry.
  const querySelectedPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    { filter: { id: { eq: value as string } }, page: { size: 1 } },
    { enabled: !!value, retry: false }
  );

  const payer = querySelectedPayerRow.data?.data?.[0];

  useEffect(() => {
    if (!payer) {
      if (value) {
        return;
      }

      setSelectValue(null);
      return;
    }

    const name = selectorSearchRowName(payer);

    setSelectValue({
      value: payer.id ?? "",
      label: name,
      labels: payer.email ? [payer.email] : [],
      avatar: {
        type: "text",
        text: name
      }
    });
  }, [payer, value]);

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayerRows.flatData ?? []).forEach(payer => {
      const name = selectorSearchRowName(payer);

      options.push({
        value: payer.id ?? "",
        label: name,
        labels: payer.email ? [payer.email] : [],
        avatar: {
          type: "text",
          text: name
        }
      });
    });

    return options;
  }, [queryPayerRows.flatData]);

  const onReachMenuEnd = useCallback(() => {
    if (queryPayerRows.hasNextPage && !queryPayerRows.isFetchingNextPage) {
      queryPayerRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayerRows.hasNextPage, queryPayerRows.isFetchingNextPage]);

  // Every page carries the same total for the current query, so page 1 answers
  // "how many clients are there" even before the rest are scrolled in.
  const totalCount = queryPayerRows.data?.pages?.[0]?.pagination?.totalSize;

  // Say out loud that the menu is a window onto a longer list, so a client that
  // is not on screen reads as "scroll or search" rather than "not a client".
  const truncationText = queryPayerRows.hasNextPage
    ? totalCount
      ? `Showing ${options.length} of ${totalCount} clients — scroll for more, or type to narrow the list.`
      : "Scroll for more clients, or type to narrow the list."
    : undefined;

  const menuFooterText =
    [truncationText, otherProps.menuFooterText].filter(Boolean).join(" ") ||
    undefined;

  return (
    <WSSelectSearch
      {...otherProps}
      value={selectValue}
      onChange={newOption => {
        if (newOption === null) {
          onChange(null);
          setSelectValue(null);
          return;
        } else if (newOption?.value) {
          onChange(newOption?.value as string);
          setSelectValue(newOption);
        }
      }}
      placeholder={otherProps.placeholder ?? "All clients"}
      options={options}
      status={queryPayerRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        (queryPayerRows.isFetching && !queryPayerRows.isFetchingNextPage) ||
        querySelectedPayerRow.isInitialLoading
      }
      onReachMenuEnd={onReachMenuEnd}
      menuFooterText={menuFooterText}
    />
  );
};
